// Query and mutation hooks over the assistant routes (spec 2026-10-21-24 §4).
// Queries go through $api so live-invalidation.ts's `assistant-threads` entry
// matches their keys.
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { AssistantMessage, AssistantPostResult, AssistantThread, AssistantThreadDetail, AssistantUndoResult } from '../../api/schema'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { useDynamicRefresh } from '../../hooks/useDynamicRefresh'
import type { PageContext } from './page-context'

const THREADS = '/api/v1/organizations/{orgCode}/assistant/threads'
const THREAD = '/api/v1/organizations/{orgCode}/assistant/threads/{uid}'

/** An API error body: `{code, title, thread_uid?}`. */
export interface AssistantError {
    code?: string
    title?: string
    thread_uid?: string
    fields?: { name: string; message: string }[]
}

export function useAssistantThreads(org: string, status: 'open' | 'closed', enabled = true) {
    return $api.useQuery('get', THREADS, { params: { path: { orgCode: org }, query: { status, limit: 50 } } }, { enabled })
}

export function useAssistantThread(org: string, uid: string | undefined) {
    return $api.useQuery('get', THREAD, { params: { path: { orgCode: org, uid: uid ?? '' } } }, { enabled: !!uid })
}

/** Invalidates every assistant query of the organization. */
export function useInvalidateAssistant(org: string) {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] !== 'get' || typeof key[1] !== 'string') return false
                if (!(key[1] as string).startsWith(THREADS)) return false
                const params = (key[2] as { params?: { path?: { orgCode?: string } } } | undefined)?.params
                return params?.path?.orgCode === org
            },
        })
}

export async function createThread(org: string): Promise<{ thread?: AssistantThread; error?: AssistantError }> {
    const { data, error } = await apiClient.POST(THREADS, { params: { path: { orgCode: org } }, body: {} })
    if (error || !data) return { error: (error ?? {}) as AssistantError }
    return { thread: data }
}

export async function closeThread(org: string, uid: string): Promise<AssistantError | undefined> {
    const { error } = await apiClient.POST(`${THREAD}/close`, { params: { path: { orgCode: org, uid } } })
    return error ? ((error ?? {}) as AssistantError) : undefined
}

export async function postMessage(
    org: string,
    uid: string,
    text: string,
    context: PageContext,
): Promise<{ result?: AssistantPostResult; error?: AssistantError }> {
    const { data, error } = await apiClient.POST(`${THREAD}/messages`, {
        params: { path: { orgCode: org, uid } },
        body: { text, context },
    })
    if (error || !data) return { error: (error ?? {}) as AssistantError }
    return { result: data }
}

export async function undoAnswer(org: string, uid: string): Promise<{ result?: AssistantUndoResult; error?: AssistantError }> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/assistant/messages/{uid}/undo', {
        params: { path: { orgCode: org, uid } },
    })
    if (error || !data) return { error: (error ?? {}) as AssistantError }
    return { result: data }
}

/** A turn is in progress while a user message is queued or running. */
export function turnInProgress(messages: AssistantMessage[] | undefined): boolean {
    return (messages ?? []).some((m) => m.turn_state === 'queued' || m.turn_state === 'running')
}

/**
 * Keeps the open thread fresh: its `assistant-threads` live scope, and
 * adaptive polling underneath while a turn is queued or running
 * (docs/frontend/dynamic-refresh.md).
 */
export function useAssistantThreadLive(thread: AssistantThreadDetail | undefined, refetch: () => Promise<unknown>) {
    const uid = thread?.uid ?? ''
    const active = turnInProgress(thread?.messages)
    useLiveSubscription({ entity: 'assistant-threads', uid }, { enabled: !!uid })
    const live = useScopeLive({ entity: 'assistant-threads', uid }) && !!uid
    const dynamicRefresh = useDynamicRefresh({ enabled: active, defaultInterval: 1500, live })
    const last = thread?.messages[thread.messages.length - 1]
    const stamp = thread ? `${thread.messages.length}|${last?.turn_state ?? ''}|${thread.live_turn?.text.length ?? 0}` : ''

    useEffect(() => {
        if (!dynamicRefresh.isEnabled || !active) return
        if (dynamicRefresh.timeRemaining !== 0) return
        void refetch()
        // keyed on timeRemaining, the documented trigger of this hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicRefresh.timeRemaining, dynamicRefresh.isEnabled, active])

    useEffect(() => {
        if (!stamp) return
        dynamicRefresh.controls.adjustRate(stamp !== dynamicRefresh.lastTimestamp, stamp)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stamp])
}
